import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useBookingStore } from '../store/bookingStore';

/**
 * Hook para gerenciar a seleção de serviços no fluxo de agendamento
 * @param {string} salonId - ID do salão 
 * @returns {Object} - Métodos e dados para gerenciar seleção de serviços
 */
export const useServiceSelection = (salonId) => {
  const { watch, setValue } = useFormContext();
  const selectedServices = watch('services') || [];
  
  // Zustand store para lógica que precisa ser compartilhada globalmente
  const addServiceToStore = useBookingStore(state => state.addService);
  const removeServiceFromStore = useBookingStore(state => state.removeService);
  const setServices = useBookingStore(state => state.setServices);
  
  /**
   * Verifica se um serviço está selecionado
   * @param {string} serviceId - ID do serviço
   * @returns {boolean} - True se estiver selecionado
   */
  const isServiceSelected = useCallback((serviceId) => {
    return selectedServices.some(s => 
      (typeof s === 'object' ? s.id === serviceId : s === serviceId)
    );
  }, [selectedServices]);
  
  /**
   * Alterna a seleção de um serviço (adiciona ou remove)
   * @param {object} service - Objeto do serviço
   */
  const toggleService = useCallback((service) => {
    const currentServices = [...selectedServices];
    const index = currentServices.findIndex(s => 
      (typeof s === 'object' ? s.id === service.id : s === service.id)
    );
    
    if (index >= 0) {
      currentServices.splice(index, 1);
      removeServiceFromStore(service.id);
    } else {
      currentServices.push(service);
      addServiceToStore(service);
    }
    
    // Atualiza tanto o React Hook Form quanto o Zustand store
    setValue('services', currentServices, { shouldValidate: true });
    setServices(currentServices);
  }, [selectedServices, setValue, addServiceToStore, removeServiceFromStore, setServices]);
  
  /**
   * Seleciona múltiplos serviços (substituindo a seleção atual)
   * @param {Array} services - Array de serviços
   */
  const selectServices = useCallback((services) => {
    setValue('services', services, { shouldValidate: true });
    setServices(services);
  }, [setValue, setServices]);
  
  /**
   * Limpa todos os serviços selecionados
   */
  const clearServices = useCallback(() => {
    setValue('services', [], { shouldValidate: true });
    setServices([]);
  }, [setValue, setServices]);
  
  return {
    selectedServices,
    toggleService,
    isServiceSelected,
    selectServices,
    clearServices,
    servicesCount: selectedServices.length
  };
}; 